import { lazy, Suspense, Component } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import './App.css';  // 2026-06-15: estaba SIN importar -> dark-mode, keyframes (spin/orbital/slideIn), label-caps y focus rings no se aplicaban
import { AuthProvider, useAuth } from './context/AuthContext';
import { ToastProvider } from './components/Toast';
import { ConfirmProvider } from './components/Confirm';
import EnvBanner from './components/EnvBanner';
import UpdateBanner from './components/UpdateBanner';
import Login from './pages/Login';
import ResetPassword from './pages/ResetPassword';
import { FONT_FAMILY } from './constants/font';

// Encuesta pública de aliados (piloto 2026-07-31): SIN login — llega por la
// liga que Rappi manda por correo. Lazy para no engordar el bundle interno.
const EncuestaAliado = lazy(() => import('./pages/EncuestaAliado'));

const Dashboard = lazy(() => import('./pages/Dashboard'));
const MapView = lazy(() => import('./pages/MapView'));
const Stores = lazy(() => import('./pages/Stores'));
const MisTiendas = lazy(() => import('./pages/MisTiendas'));  // inicio del KAM (2026-10-11)
const StoreDetail = lazy(() => import('./pages/StoreDetail'));
const Inventario = lazy(() => import('./pages/Inventario'));
const Reportes = lazy(() => import('./pages/Reportes'));
const Validacion = lazy(() => import('./pages/Validacion'));
const Admin = lazy(() => import('./pages/Admin'));
const Campo = lazy(() => import('./pages/Campo'));
const CIC = lazy(() => import('./pages/CIC'));
const RutasAsignadas = lazy(() => import('./pages/RutasAsignadas'));
const Rutas = lazy(() => import('./pages/Rutas'));
const Rutas2 = lazy(() => import('./pages/Rutas2'));
const HeatmapRappi = lazy(() => import('./pages/HeatmapRappi'));
const HotZones = lazy(() => import('./pages/HotZones'));
const Calendario = lazy(() => import('./pages/Calendario'));
const SetupProfile = lazy(() => import('./pages/SetupProfile'));
const InventarioRappi = lazy(() => import('./pages/InventarioRappi'));
const Hunter = lazy(() => import('./pages/Hunter'));
const MiCuenta = lazy(() => import('./pages/MiCuenta'));

const PageLoader = () => (
  <div style={{
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    height: '100vh', gap: 12, color: '#6B7280', fontFamily: FONT_FAMILY,
  }}>
    <div style={{
      width: 20, height: 20,
      border: '2.5px solid #E5E7EB', borderTopColor: '#FF6B00',
      borderRadius: '50%', animation: 'spin 0.8s linear infinite',
    }} />
    <span>Cargando...</span>
  </div>
);

class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null, info: null };
  }
  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }
  // "Loading chunk N failed" = hubo un DEPLOY mientras la app estaba abierta:
  // el bundle viejo (en memoria del navegador) pide un chunk lazy que ya no
  // existe en el servidor. No es un bug — se cura recargando (trae el index
  // nuevo). Visto por Jose en staging 2026-07-02 al entrar a Inventario.
  static isChunkError(error) {
    const msg = error?.message || String(error || '');
    return error?.name === 'ChunkLoadError' || /Loading (CSS )?chunk .+ failed/i.test(msg);
  }
  componentDidCatch(error, info) {
    // Log to console so F12 shows the full stack — critical for prod debugging
    console.error('ErrorBoundary caught:', error, info);
    // Auto-recargar UNA vez ante chunk viejo (flag con timestamp en
    // sessionStorage para no ciclar si el error persistiera de verdad).
    if (ErrorBoundary.isChunkError(error)) {
      try {
        const last = parseInt(sessionStorage.getItem('chunk_reload_at') || '0', 10);
        if (Date.now() - last > 60000) {
          sessionStorage.setItem('chunk_reload_at', String(Date.now()));
          window.location.reload();
          return;
        }
      } catch { /* sessionStorage bloqueado: cae al UI de error normal */ }
    }
    this.setState({ info });
  }
  render() {
    if (this.state.hasError) {
      const msg = this.state.error?.message || String(this.state.error || 'Error desconocido');
      const stack = this.state.info?.componentStack || this.state.error?.stack || '';
      return (
        <div style={{
          display: 'flex', flexDirection: 'column', alignItems: 'center',
          justifyContent: 'center', minHeight: '100vh', fontFamily: FONT_FAMILY,
          color: '#374151', gap: 16, padding: 32,
        }}>
          <div style={{
            width: 56, height: 56, borderRadius: 16,
            background: '#FEF2F2', display: 'flex', alignItems: 'center',
            justifyContent: 'center', fontSize: 24,
          }}>!</div>
          <h2 style={{ margin: 0, fontSize: 18, fontWeight: 600 }}>Algo salio mal</h2>
          <p style={{ margin: 0, fontSize: 14, color: '#6B7280', textAlign: 'center', maxWidth: 400 }}>
            Ocurrio un error inesperado. Intenta recargar la pagina.
          </p>
          <details style={{ maxWidth: 600, width: '100%', fontSize: 12, color: '#991B1B' }}>
            <summary style={{ cursor: 'pointer', fontWeight: 600 }}>Detalles del error</summary>
            <pre style={{
              marginTop: 8, padding: 12, background: '#FEF2F2', borderRadius: 8,
              whiteSpace: 'pre-wrap', wordBreak: 'break-word', fontSize: 11,
              maxHeight: 300, overflowY: 'auto',
            }}>
              {msg}
              {stack ? '\n\n' + stack : ''}
            </pre>
          </details>
          <div style={{ display: 'flex', gap: 10 }}>
            <button
              onClick={() => window.location.reload()}
              style={{
                padding: '10px 24px', border: 'none', borderRadius: 8,
                background: '#FF6B00', color: '#fff', fontSize: 14,
                fontWeight: 600, cursor: 'pointer', fontFamily: FONT_FAMILY,
              }}
            >
              Recargar
            </button>
            <button
              onClick={() => {
                try { localStorage.removeItem('token'); } catch {}
                window.location.href = '/login';
              }}
              style={{
                padding: '10px 24px', border: '1px solid #E5E7EB', borderRadius: 8,
                background: '#fff', color: '#374151', fontSize: 14,
                fontWeight: 600, cursor: 'pointer', fontFamily: FONT_FAMILY,
              }}
            >
              Cerrar sesion
            </button>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

// Role-based access control
// /heatmaprappi: solo admin (data de flujo peatonal sensible comercialmente).
// /calendario: solo admin (planeacion de implementacion sensible).
// /hotzones: solo admin (cruce heatmap × stores POP, oportunidades comerciales).
const ROLE_ACCESS = {
  admin: ['/', '/mapa', '/stores', '/inventario', '/reportes', '/validacion', '/campo', '/cic', '/rutas-asignadas', '/rutas', '/rutas2', '/admin', '/heatmaprappi', '/calendario', '/hotzones', '/inventario-rappi', '/hunter'],
  // HUNTER: Inventario (solicitudes de material) + Hunter (levantamiento de stores)
  // + detalle de store (2026-06-30: click en un registro abre su detalle).
  hunter: ['/hunter', '/inventario', '/stores/detail'],
  // IMPLEMENTACION regular (sin flags): su trabajo de campo + ruteo + Mi cuenta.
  // ⚠️ FUENTE DE VERDAD = el branch dedicado de ProtectedRoute (implAllowed), que
  // intercepta a TODO role==='implementacion' ANTES de este fallback. Esta lista
  // NO se alcanza hoy; se conserva solo como red de seguridad (defense-in-depth)
  // por si el branch se removiera. Si editas una, edita la otra. Pedido Jose 2026-06-25.
  implementacion: ['/campo', '/rutas-asignadas', '/rutas', '/cuenta'],
  // KAM regular (no Lider): Dashboard/Reportes/Mapa/Stores de su zona + Calendario
  // + Mi cuenta (cambiar contraseña). Validacion la ven SOLO los Lideres.
  kam: ['/', '/mis-tiendas', '/mapa', '/stores', '/reportes', '/calendario', '/cuenta'],
  cliente: ['/', '/mapa', '/reportes'],
  // Equipo Repartidores (gestor y entregador): SOLO el modulo Inventario Rappi
  // + Mi cuenta. Documental — el gate real es el branch dedicado de
  // ProtectedRoute (el fallback generico deja pasar '/' y estos perfiles NO
  // deben ver el Dashboard POP).
  rts_inventario: ['/inventario-rappi', '/cuenta'],
};

// Supervisor (no-KAM) override: /rutas-asignadas + /validacion + /mapa
// (scoped a sus ciudades por el backend) + detalle de stores (abierto
// desde links en otras pantallas, no lista /stores).
// + /campo (pedido 2026-05-07): el supervisor tambien ejecuta rutas que el
// mismo se asigna, asi que necesita la pestana Campo.
// + /cuenta: cambiar su propia contraseña.
// NOTA (Jose 2026-06-25): se quitó /inventario — Implementación ya no ve
// Inventario en ningún perfil. Las Solicitudes Hunter las maneja el rol Hunter
// en su propia sección, no el supervisor de implementación.
const SUPERVISOR_ACCESS = ['/rutas-asignadas', '/validacion', '/stores/detail', '/mapa', '/campo', '/cic', '/cuenta'];

// Lider = KAM + is_supervisor. Acceso ampliado con /admin (scoped a su zona).
const LIDER_ACCESS = ['/', '/mis-tiendas', '/mapa', '/stores', '/reportes', '/validacion', '/calendario', '/admin'];

const isLider = (user) => user && user.role?.toLowerCase() === 'kam' && user.is_supervisor;

// Exportada: SetupProfile.js la reusa para mandar al usuario a SU home tras
// el primer cambio de password (antes navegaba a '/' hardcoded y los perfiles
// sin Dashboard rebotaban).
export function getDefaultRoute(user) {
  if (!user) return '/login';
  if (user.must_change_password) return '/setup-profile';
  // Equipo Repartidores (gestor o entregador): su unica pantalla es el modulo
  if (user.role?.toLowerCase() === 'rts_inventario') return '/inventario-rappi';
  if (user.campo_only) return '/campo';
  // RTS (Repartidores): arranca directo en Campo, su pestana principal
  if (user.is_rts && user.role?.toLowerCase() === 'implementacion') return '/campo';
  // HUNTER: arranca en su seccion de levantamiento de stores.
  if (user.role?.toLowerCase() === 'hunter') return '/hunter';
  if (isLider(user)) return '/';  // Lider arranca en Dashboard
  // KAM regular arranca en SUS tiendas (Jose 2026-10-11): sus numeros y la
  // bandeja Por decidir, no el Dashboard nacional.
  if (user.role?.toLowerCase() === 'kam') return '/mis-tiendas';
  // Supervisor de implementacion arranca en Rutas Asignadas (acotado a impl: un
  // coordinador Hunter es is_supervisor pero ya retorno /hunter arriba).
  if (user.is_supervisor && user.role?.toLowerCase() === 'implementacion') return '/rutas-asignadas';
  // Implementacion regular: no ve Dashboard, arranca en Campo.
  if (user.role?.toLowerCase() === 'implementacion') return '/campo';
  return '/';
}

function ProtectedRoute({ children, path = '/', allowCampoOnly = false }) {
  const { user, loading } = useAuth();
  if (loading) return <PageLoader />;
  if (!user) return <Navigate to="/login" />;
  // Primer login: debe configurar nombre + password antes de nada mas
  if (user.must_change_password && path !== '/setup-profile') {
    return <Navigate to="/setup-profile" />;
  }
  // /setup-profile accesible solo si must_change_password=true; sino redirect
  if (path === '/setup-profile') {
    if (!user.must_change_password) return <Navigate to={getDefaultRoute(user)} />;
    return children;
  }
  // Equipo Repartidores (rol rts_inventario, gestor o entregador): SOLO el
  // modulo Inventario Rappi + Mi cuenta. Branch DEDICADO antes del fallback
  // generico — el fallback deja pasar '/' y estos perfiles NO deben ver el
  // Dashboard POP ni el resto de la plataforma.
  if (user.role?.toLowerCase() === 'rts_inventario') {
    const RTS_INV_ALLOWED = ['/inventario-rappi', '/cuenta', '/setup-profile'];
    if (!RTS_INV_ALLOWED.includes(path)) return <Navigate to="/inventario-rappi" />;
    return children;
  }
  // Colocadores (campo_only) solo pueden acceder a /campo.
  // EXCEPCION: si tiene puede_generar_rutas, ademas puede entrar a /rutas
  // y /rutas-asignadas (mismo gate que Sidebar.js).
  if (user.campo_only && !allowCampoOnly) {
    if (path === '/cuenta') return children;  // cambiar su propia contraseña
    if (user.puede_generar_rutas && (path === '/rutas' || path === '/rutas2' || path === '/rutas-asignadas' || path === '/mapa')) {
      return children;  // generador de rutas: ademas /mapa + /rutas + /rutas2
    }
    return <Navigate to="/campo" />;
  }
  // RTS (Repartidores) — subtipo de IMPLEMENTACION. Solo /campo + /mapa.
  // Ademas /stores/detail (para abrir un store del mapa). Pedido 2026-05-07.
  if (user.is_rts && user.role?.toLowerCase() === 'implementacion') {
    const RTS_ALLOWED = ['/campo', '/mapa', '/stores/detail'];
    if (path === '/' || !RTS_ALLOWED.includes(path)) return <Navigate to="/campo" />;
    return children;
  }
  // Lider (KAM + is_supervisor): acceso tipo KAM + /admin
  if (isLider(user)) {
    if (path === '/stores/detail' && LIDER_ACCESS.includes('/stores')) return children;
    if (path !== '/' && !LIDER_ACCESS.includes(path)) return <Navigate to={getDefaultRoute(user)} />;
    return children;
  }
  // Supervisor de IMPLEMENTACION (no-KAM): solo rutas-asignadas + validacion + mapa.
  // EXCEPCION: si tiene puede_generar_rutas, ademas puede entrar a /rutas
  // para generar (mismo gate que en Sidebar.js — sin esto, el link aparecia
  // en el menu pero al click te mandaba a /rutas-asignadas).
  // ACOTADO a role==='implementacion' (Jose 2026-06-25): is_supervisor tambien
  // existe en HUNTERS (coordinador = hunter+is_supervisor). Sin este filtro, un
  // coordinador Hunter caia aqui y quedaba BLOQUEADO de /hunter + /inventario.
  if (user.is_supervisor && user.role?.toLowerCase() === 'implementacion') {
    const allowedForSupervisor = user.puede_generar_rutas
      ? [...SUPERVISOR_ACCESS, '/rutas', '/rutas2']
      : SUPERVISOR_ACCESS;
    if (!allowedForSupervisor.includes(path)) return <Navigate to="/rutas-asignadas" />;
    return children;
  }
  // IMPLEMENTACION regular (ya pasaron campo_only/rts/supervisor): su trabajo de
  // campo + ruteo asignado + Mi cuenta. NO Dashboard/Reportes/Inventario/Stores/
  // Validacion. Mapa SOLO si es generador de rutas. Pedido Jose 2026-06-25.
  // (gate explicito — '/' NO se permite, a diferencia del fallback generico).
  if (user.role?.toLowerCase() === 'implementacion') {
    const implAllowed = ['/campo', '/rutas-asignadas', '/rutas', '/cuenta'];
    if (user.puede_generar_rutas) implAllowed.push('/mapa', '/rutas2');  // generador de rutas
    if (!implAllowed.includes(path)) return <Navigate to="/campo" />;
    return children;
  }
  // Role-based route protection
  const role = user.role?.toLowerCase();
  const allowed = ROLE_ACCESS[role] || ['/'];
  // /stores/detail es subruta de /stores — cualquier rol con /stores puede verla
  if (path === '/stores/detail' && allowed.includes('/stores')) return children;
  if (path !== '/' && !allowed.includes(path)) return <Navigate to={getDefaultRoute(user)} />;
  return children;
}

function AppRoutes() {
  const { user, loading } = useAuth();
  if (loading) return null;

  return (
    <Suspense fallback={<PageLoader />}>
      <Routes>
        <Route path="/login" element={user ? <Navigate to={getDefaultRoute(user)} /> : <Login />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        {/* Pública (aliados, sin login) — la liga que Rappi manda por correo */}
        <Route path="/encuesta" element={<EncuestaAliado />} />
        <Route path="/setup-profile" element={<ProtectedRoute path="/setup-profile"><SetupProfile /></ProtectedRoute>} />
        <Route path="/" element={<ProtectedRoute path="/"><Dashboard /></ProtectedRoute>} />
        <Route path="/mapa" element={<ProtectedRoute path="/mapa"><MapView /></ProtectedRoute>} />
        <Route path="/stores" element={<ProtectedRoute path="/stores"><Stores /></ProtectedRoute>} />
        <Route path="/mis-tiendas" element={<ProtectedRoute path="/mis-tiendas"><MisTiendas /></ProtectedRoute>} />
        <Route path="/stores/:id" element={<ProtectedRoute path="/stores/detail"><StoreDetail /></ProtectedRoute>} />
        <Route path="/inventario" element={<ProtectedRoute path="/inventario"><Inventario /></ProtectedRoute>} />
        <Route path="/reportes" element={<ProtectedRoute path="/reportes"><Reportes /></ProtectedRoute>} />
        <Route path="/validacion" element={<ProtectedRoute path="/validacion"><Validacion /></ProtectedRoute>} />
        <Route path="/campo" element={<ProtectedRoute path="/campo" allowCampoOnly><Campo /></ProtectedRoute>} />
        <Route path="/cic" element={<ProtectedRoute path="/cic"><CIC /></ProtectedRoute>} />
        <Route path="/rutas-asignadas" element={<ProtectedRoute path="/rutas-asignadas"><RutasAsignadas /></ProtectedRoute>} />
        <Route path="/rutas" element={<ProtectedRoute path="/rutas"><Rutas /></ProtectedRoute>} />
        <Route path="/rutas2" element={<ProtectedRoute path="/rutas2"><Rutas2 /></ProtectedRoute>} />
        <Route path="/admin" element={<ProtectedRoute path="/admin"><Admin /></ProtectedRoute>} />
        <Route path="/heatmaprappi" element={<ProtectedRoute path="/heatmaprappi"><HeatmapRappi /></ProtectedRoute>} />
        <Route path="/hotzones" element={<ProtectedRoute path="/hotzones"><HotZones /></ProtectedRoute>} />
        <Route path="/calendario" element={<ProtectedRoute path="/calendario"><Calendario /></ProtectedRoute>} />
        <Route path="/inventario-rappi" element={<ProtectedRoute path="/inventario-rappi"><InventarioRappi /></ProtectedRoute>} />
        <Route path="/hunter" element={<ProtectedRoute path="/hunter"><Hunter /></ProtectedRoute>} />
        <Route path="/cuenta" element={<ProtectedRoute path="/cuenta"><MiCuenta /></ProtectedRoute>} />
        <Route path="*" element={<Navigate to="/" />} />
      </Routes>
    </Suspense>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <ToastProvider>
          <ConfirmProvider>
          <ErrorBoundary>
            {/* Banner de ambiente (staging/dev). En produccion no renderea nada. */}
            <EnvBanner />
            {/* Detector de deploy nuevo: polling cada 5 min al asset-manifest.json.
                Si el hash del bundle cambio, muestra banner pidiendo recargar. */}
            <UpdateBanner />
            <AppRoutes />
          </ErrorBoundary>
          </ConfirmProvider>
        </ToastProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
